<template>
  <div v-if="items" class="c-list-results">
    <div
      v-for="(item, index) in items"
      :key="item.id"
      class="c-list-result"
      :class="{
        hideFeatured: index === featuredIndex
      }"
    >
      <item-image
        :image="item.item.featuredImage.jss.value"
        :alt="getResourceTitle(item.item)"
        :url="item.item.url"
        :template="item.templateName"
        :topics="item.owners"
        :topicImageSrc="topicImageSrc"
      />
      <div class="c-list-content">
        <div
          class="c-date-container"
          :class="{
            isFeatured: item?.item?.featuredStory?.value === '1'
          }"
        >
          <item-featured-badge
            :item="item.item"
            v-if="item.templateName === 'CIS-Resource-Newsletter-RouteType'"
          />
          <item-date :item="item.item" :useDateIssued="useDateIssued" />
          <webinar-date
            :item="item.item"
            v-if="item.templateName === 'CIS-Resource-Webinar-RouteType'"
          />
          <template v-else></template>
        </div>
        <div class="c-list-clear">
          <a :href="item.item.url" class="c-list-link">{{ getResourceTitle(item.item) }}</a>
        </div>
        <benchmark-excerpt
          v-if="item.templateName === 'CIS-Resource-Benchmark-RouteType'"
          :item="item.item"
        />
        <item-excerpt v-else :item="item.item" :maxCharacters="145" />
        <div class="c-list-badges">
          <span v-for="owner in showSelectedBadges(item.item.owners.targetItems)" :key="owner.id">
            <item-badge :ownerName="owner.title.value" />
          </span>
        </div>
      </div>
      <hr />
    </div>
  </div>
</template>

<script>
import ItemImage from './CIS-Resource-PagedListing-Image.vue';
import ItemFeaturedBadge from './CIS-Resource-PagedListing-Newsletter.vue';
import ItemDate from './CIS-Resource-PagedListing-Date.vue';
import WebinarDate from './CIS-Resource-PagedListing-Webinar.vue';
import ItemExcerpt from './CIS-Resource-PagedListing-Excerpt.vue';
import ItemBadge from './CIS-Resource-PagedListing-Badge.vue';
import BenchmarkExcerpt from './CIS-Resource-PagedListing-BenchmarkExcerpt.vue';

export default {
  name: 'CIS-Resource-PagedListing-Items',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    badges: {
      type: Array,
      default: () => []
    },
    badgeSize: {
      type: Number
    },
    selectedTopics: {
      type: Array,
      default: () => []
    },
    topicImageSrc: {
      type: Function,
      default: () => undefined
    },
    useDateIssued: {
      type: Boolean,
      default: false
    },
    featuredIndex: {
      type: Number
    }
  },
  components: {
    ItemImage,
    ItemFeaturedBadge,
    ItemDate,
    WebinarDate,
    ItemExcerpt,
    ItemBadge,
    BenchmarkExcerpt
  },
  methods: {
    getResourceTitle(item) {
      // If the route type is 'advisory' then prefix the title with the advisory number
      if (item.__typename === 'CISResourceAdvisoryRouteType') {
        return `${item.advisoryNumber?.value}: ${item.resourceTitle?.value}`;
      }
      return item?.resourceTitle?.value;
    },
    showSelectedBadges(item) {
      const selected = [];
      const unselected = [];
      const filterIds = this.selectedTopics.map((obj) => obj.id);

      item.forEach((item) => {
        if (filterIds.includes(item.id)) {
          selected.push(item);
        } else {
          unselected.push(item);
        }
      });

      const sortedItems = [...selected, ...unselected] || [];

      if (selected.length > 5) {
        return selected.filter((item) => this.badges?.includes(item.id));
      } else {
        const selectedBadges = sortedItems.filter((item) => this.badges?.includes(item.id));
        const maxSelectedBadges = selectedBadges?.slice(0, this.badgeSize);
        return maxSelectedBadges;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.c-list-results {
  > div {
    display: flex;
  }

  .c-list-result {
    padding: 3rem 0 2rem 0;
    border-bottom: solid 0.1rem $divider;

    @include desktop-up {
      border: none;
      padding: 0;
      margin-bottom: 6.2rem;
    }

    .c-list-image {
      width: 28%;
      flex-shrink: 0;
      height: auto;
      margin-right: 1.5rem;

      @include desktop-up {
        margin-right: 5rem;
      }

      img {
        width: 100%;
        height: auto;
      }

      &:deep() {
        img {
          width: 100%;
          height: auto;
        }
      }
    }

    .c-date-container {
      float: left;

      &.isFeatured {
        float: none;
        margin-bottom: 1rem;

        @include tablet-up {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .c-list-date {
          margin: 0;
          float: none;
          @include tablet-up {
            position: relative;
            top: -0.1rem;
          }
        }
      }
    }

    .c-list-date {
      @include font-styles('Arial Narrow', 1.4rem, 600, normal, normal, 0, $dark-blue);
      text-transform: uppercase;
      margin-bottom: 1rem;
      margin-right: 0.75rem;

      @include large-desktop-up {
        float: left;
      }
    }

    .c-list-link {
      display: inline-block;
      @include font-styles('Arial', 1.6rem, 200, normal, 2rem, 0, $blue);
      margin-bottom: 1rem;

      @include desktop-up {
        font-size: 2.8rem;
        line-height: 3.5rem;
      }
    }

    .c-list-badges {
      line-height: normal;
      display: flex;
      flex-wrap: wrap;
      margin-top: 1rem;
    }

    .c-list-clear {
      clear: both;
    }

    &.hideFeatured {
      display: none;
    }
  }

  .c-list-paging {
    display: flex;
    justify-content: space-around;
  }

  &.featured-only {
    margin-bottom: 6.2rem;

    .c-list-result {
      margin-bottom: 0;
    }
  }
}
</style>
